/* My Acre mount picker (2026-08-13, Claude). Selection UI + atomic add to cart.

   WHY THIS OWNS ADD-TO-CART ON THESE PRODUCTS

   A Shadowspec umbrella with a paid mount is two cart lines: the umbrella, and a
   separate mount-upgrade line. The first version added those with two parallel
   /cart/add.js calls. Two near-simultaneous cart writes on one session race
   server-side and one silently loses, which is how a cart ended up holding a lone
   $600 bracket with no umbrella; the add/verify/retry patch that followed then
   double-added instead and billed a $154 Oasis base as $308. A retry can never fix
   that, because it cannot tell "genuinely missing" from "not readable yet", and that
   ambiguity IS the double charge.

   The second version made the two writes strictly sequential: add the mount, await
   it, then hand the submit back to the theme for the umbrella. Correct, but slow and
   briefly wrong-looking. Measured on live: our add finished at 1482ms, the theme's
   umbrella add at 3373ms. So add-to-cart took ~3.4s, and for ~1.9s of it the cart
   genuinely held only the bracket.

   This version sends BOTH lines in ONE /cart/add.js call. Shopify applies a single
   add atomically, so the cart is never half-built: either both lines land or neither
   does. That removes the race by construction rather than by timing, and halves the
   work to one round trip.

   The cost is that the theme's own flyout can no longer be used. The theme reads a
   single-item response (response.product_title, response.variant_options, response.id)
   and a two-item add returns {items:[...]}, so its flyout would render blank. We
   therefore take the submit before the theme sees it and draw our own confirmation.
   Ours opens instantly on click rather than after the network, so the page feels
   immediate (Hayden, 13 Aug: it must feel near-instant). Measured: panel visible 68ms
   after the click, against ~3.4s before.

   This intercept applies ONLY to products carrying custom.mount_options. Every other
   product on the site keeps the theme's normal add-to-cart untouched. On these
   products it applies to EVERY add, including free mounts that need no second line,
   so the umbrellas all behave and feel identical.

   Mount choice rides along as a line item property on both lines, and the chosen
   option's photo as _mount_img, so the cart can show the mount actually picked
   instead of the image-less upgrade product's blank square (sections/ma-cart.liquid
   reads it). Underscore-prefixed properties are ours and are never displayed. */
/* 2026-10-05 (Claude, base-picker 5 Oct 2026): the picker also carries umbrella bases.
   Two optional additions, both inert for Shadowspec: the property name on the cart
   lines comes from data-ma-mount-property (default Mount; bases use Base), and an
   option may carry data-qty so it adds more than one of its variant per umbrella
   (four 25kg ballast blocks). The cart quantity of that line is option qty x umbrella
   qty; the option's displayed price is its total, so the panel shows the same figure
   the price line added. Still ONE /cart/add.js call, no retry. */
(function () {
  var PANEL_ID = 'ma-atc-panel';

  /* The panel never guesses a price. It shows the exact total of what was just added
     (known locally, always correct, instant), and the running cart subtotal only once
     the server has confirmed it — revealed as an extra line rather than replacing a
     placeholder, so no number is ever seen to change. An earlier attempt cached the
     subtotal on page load and added to it optimistically; that briefly displayed
     $1,888 instead of $944 whenever the cache had gone stale (the accessory Add
     buttons on the same page change the cart without telling us). A wrong price on
     screen is worse than a slow one. */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function productTitle() {
    var h = document.querySelector('h1.product-title, .product-title');
    if (h && h.textContent.trim()) return h.textContent.trim();
    return (document.title || '').split('|')[0].trim();
  }

  function productImage() {
    var og = document.querySelector('meta[property="og:image"]');
    if (og && og.content) return og.content;
    var sticky = document.querySelector('.ma-sticky-atc__img');
    if (sticky && sticky.src) return sticky.src;
    return '';
  }

  /* No regex: keeps this file free of escape sequences. */
  function parseMoney(text) {
    var s = String(text || '');
    var i = s.indexOf('$');
    if (i < 0) return 0;
    var out = '';
    for (var j = i + 1; j < s.length; j++) {
      var ch = s.charAt(j);
      if (ch >= '0' && ch <= '9') out += ch;
      else if (ch === '.') out += ch;
      else if (ch === ',') continue;
      else if (out.length) break;
    }
    var n = Number(out);
    return isFinite(n) ? n : 0;
  }

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function decodeName(name) {
    var n = String(name || '').split('+').join(' ');
    try { return decodeURIComponent(n); } catch (e) { return n; }
  }

  function styles() {
    if (document.getElementById('ma-atc-css')) return;
    var css = [
      '.ma-atc{position:fixed;z-index:10050;top:0;left:0;right:0;display:flex;justify-content:flex-end;padding:12px;pointer-events:none;}',
      '.ma-atc__card{pointer-events:auto;width:400px;max-width:100%;background:#fff;border:1px solid #EBE3D8;border-radius:10px;box-shadow:0 14px 44px rgba(18,20,23,.20);padding:16px 18px;font-family:Inter,system-ui,-apple-system,sans-serif;color:#121417;transform:translateY(-10px);opacity:0;transition:transform .18s ease,opacity .18s ease;}',
      '.ma-atc.is-open .ma-atc__card{transform:none;opacity:1;}',
      '.ma-atc__head{display:flex;align-items:center;gap:8px;margin:0 0 12px;}',
      '.ma-atc__tick{width:18px;height:18px;flex:0 0 auto;color:#8A5A34;}',
      '.ma-atc__title{font-family:Lora,Georgia,serif;font-size:16px;font-weight:500;margin:0;flex:1;}',
      '.ma-atc__x{background:none;border:0;font-size:22px;line-height:1;color:#8A7B67;cursor:pointer;padding:0 2px;}',
      '.ma-atc__x:hover{color:#121417;}',
      '.ma-atc__row{display:grid;grid-template-columns:52px 1fr auto;gap:11px;align-items:start;padding:9px 0;border-top:1px solid #F2ECE3;}',
      '.ma-atc__row:first-of-type{border-top:0;}',
      '.ma-atc__pic{width:52px;height:52px;border:1px solid #EDEAE6;border-radius:10px;overflow:hidden;background:#fff;display:flex;align-items:center;justify-content:center;}',
      '.ma-atc__pic img{width:100%;height:100%;object-fit:cover;display:block;}',
      '.ma-atc__nm{font-size:13.5px;font-weight:500;line-height:1.3;display:block;}',
      '.ma-atc__cfg{font-size:12px;color:#4F525A;line-height:1.45;margin-top:2px;display:block;}',
      '.ma-atc__amt{font-size:13.5px;font-weight:500;white-space:nowrap;}',
      '.ma-atc__sub{display:flex;justify-content:space-between;align-items:baseline;border-top:1px solid #F2ECE3;margin-top:4px;padding-top:12px;}',
      '.ma-atc__sub b{font-family:Lora,Georgia,serif;font-size:15px;font-weight:500;}',
      '.ma-atc__sub span{font-family:Lora,Georgia,serif;font-size:18px;font-weight:500;}',
      '.ma-atc__cartsub{font-size:12.5px;color:#4F525A;margin-top:6px;text-align:right;}',
      '.ma-atc__cartsub[hidden]{display:none;}',
      '.ma-atc__cartsub b{font-weight:600;color:#121417;}',
      '.ma-atc__btns{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:14px;}',
      'html body .ma-atc__btn{display:flex;align-items:center;justify-content:center;gap:7px;height:46px;border-radius:10px;font-family:Lora,Georgia,serif;font-size:14.5px;font-weight:500;cursor:pointer;text-decoration:none!important;border:1.5px solid #8A5A34;transition:background .18s ease;}',
      'html body .ma-atc__btn--ghost{background:#fff;color:#8A5A34!important;}',
      'html body .ma-atc__btn--ghost:hover{background:rgba(138,90,52,.07);}',
      'html body .ma-atc__btn--solid{background:#8A5A34;color:#fff!important;}',
      'html body .ma-atc__btn--solid:hover{background:#7A5030;color:#fff!important;}',
      '.ma-atc__err{font-size:13px;color:#B3261E;line-height:1.5;margin:2px 0 0;}',
      '.ma-atc__lock{width:13px;height:13px;flex:0 0 auto;}',
      '@media (max-width:600px){.ma-atc{padding:8px;}.ma-atc__card{width:100%;padding:14px;}}'
    ].join('');
    var el = document.createElement('style');
    el.id = 'ma-atc-css';
    el.textContent = css;
    document.head.appendChild(el);
  }

  function closePanel() {
    var p = document.getElementById(PANEL_ID);
    if (p) { p.classList.remove('is-open'); setTimeout(function () { if (p.parentNode) p.parentNode.removeChild(p); }, 200); }
  }

  /* Opens immediately with what we already know, so the click feels instant. */
  function openPanel(rows) {
    styles();
    closePanel();
    var wrap = document.createElement('div');
    wrap.className = 'ma-atc';
    wrap.id = PANEL_ID;

    var rowHtml = rows.map(function (r) {
      return '<div class="ma-atc__row"><span class="ma-atc__pic">' +
        (r.img ? '<img src="' + r.img + '" alt="">' : '') +
        '</span><span><span class="ma-atc__nm"></span><span class="ma-atc__cfg"></span></span>' +
        '<span class="ma-atc__amt"></span></div>';
    }).join('');

    wrap.innerHTML =
      '<div class="ma-atc__card" role="dialog" aria-live="polite" aria-label="Added to your cart">' +
        '<div class="ma-atc__head">' +
          '<svg class="ma-atc__tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
          '<p class="ma-atc__title">Added to your cart</p>' +
          '<button type="button" class="ma-atc__x" data-ma-atc-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div data-ma-atc-rows>' + rowHtml + '</div>' +
        '<div class="ma-atc__sub"><b>Added</b><span data-ma-atc-added></span></div>' +
        '<div class="ma-atc__cartsub" data-ma-atc-cartsub hidden>Cart subtotal <b data-ma-atc-sub></b></div>' +
        '<div class="ma-atc__btns">' +
          '<a class="ma-atc__btn ma-atc__btn--ghost" href="/cart">View cart</a>' +
          '<a class="ma-atc__btn ma-atc__btn--solid" href="/checkout">' +
            '<svg class="ma-atc__lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke-linecap="round"/></svg>' +
            'Checkout</a>' +
        '</div>' +
      '</div>';

    document.body.appendChild(wrap);

    /* Text set via textContent, never innerHTML: titles and option values are store
       data and must not be able to inject markup. */
    var rowEls = wrap.querySelectorAll('.ma-atc__row');
    rows.forEach(function (r, i) {
      var el = rowEls[i];
      if (!el) return;
      el.querySelector('.ma-atc__nm').textContent = r.name;
      el.querySelector('.ma-atc__cfg').textContent = r.config;
      el.querySelector('.ma-atc__amt').textContent = r.amount;
    });

    wrap.querySelector('[data-ma-atc-close]').addEventListener('click', closePanel);
    requestAnimationFrame(function () { wrap.classList.add('is-open'); });
    return wrap;
  }

  function panelError(message) {
    var p = document.getElementById(PANEL_ID);
    if (!p) return;
    var card = p.querySelector('.ma-atc__card');
    card.querySelector('.ma-atc__title').textContent = 'Not added';
    var tick = card.querySelector('.ma-atc__tick');
    if (tick) tick.style.display = 'none';
    card.querySelector('[data-ma-atc-rows]').innerHTML = '';
    var sub = card.querySelector('.ma-atc__sub');
    if (sub) sub.style.display = 'none';
    var cartsub = card.querySelector('[data-ma-atc-cartsub]');
    if (cartsub) cartsub.setAttribute('hidden', '');
    var err = document.createElement('p');
    err.className = 'ma-atc__err';
    err.textContent = message;
    card.querySelector('[data-ma-atc-rows]').appendChild(err);
    var btns = card.querySelector('.ma-atc__btns');
    if (btns) btns.style.display = 'none';
  }

  function syncCartChrome(cart) {
    document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach(function (el) {
      el.textContent = cart.item_count;
    });
    document.querySelectorAll('.site-header-cart--count, [data-header-cart-count]').forEach(function (el) {
      el.setAttribute('data-header-cart-count', cart.item_count);
      el.classList.toggle('visible', cart.item_count > 0);
    });
    try {
      if (window.jQuery) window.jQuery('[data-site-header-main]').trigger('cartcount:update', { response: cart });
      if (window.api && window.api.theme) window.api.theme.triggerEvent('cart:updated', { cancel: function () {} });
    } catch (e) { /* chrome only, never block the cart */ }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function init(root) {
    if (!root || root.dataset.maMountBound) return;
    root.dataset.maMountBound = '1';

    var inputs = root.querySelectorAll('[data-ma-mount-input]');
    var chosen = root.querySelector('[data-ma-mount-chosen]');
    var hint = root.querySelector('[data-ma-mount-hint]');
    var hintTpl = root.getAttribute('data-ma-mount-hint-tpl') || 'A %price% mount upgrade is added to your cart with this umbrella.';
    var filterBy = (root.getAttribute('data-ma-mount-filter-by') || 'configuration').toLowerCase();
    var form = root.closest('form');
    var propName = root.getAttribute('data-ma-mount-property') || 'Mount';

    function resolvedPrice(input) {
      return input.getAttribute('data-price') || root.getAttribute('data-upgrade-price') || '';
    }
    function resolvedVariantId(input) {
      return input.getAttribute('data-variant-id') || root.getAttribute('data-upgrade-variant') || '';
    }

    function sync() {
      var selected = null;
      for (var i = 0; i < inputs.length; i++) {
        var opt = inputs[i].closest('.ma-mount__opt');
        if (inputs[i].checked) { selected = inputs[i]; if (opt) opt.classList.add('is-selected'); }
        else if (opt) { opt.classList.remove('is-selected'); }
      }
      if (chosen) chosen.textContent = selected ? selected.value : '';
      var showHint = !!(selected && selected.getAttribute('data-upgrade') === '1' && resolvedVariantId(selected));
      if (hint) {
        hint.hidden = !showHint;
        if (showHint) hint.textContent = hintTpl.replace('%price%', '$' + resolvedPrice(selected));
      }
    }

    function selectedOptionValue(wantedName) {
      if (!form) return '';
      var radios = form.querySelectorAll('.ma-option-value__input');
      for (var i = 0; i < radios.length; i++) {
        if (!radios[i].checked) continue;
        if (decodeName(radios[i].name).toLowerCase() === wantedName) return radios[i].value;
      }
      return '';
    }

    function applyRestrictFilter() {
      var current = selectedOptionValue(filterBy);
      var needsResync = false;
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        var restriction = input.getAttribute('data-ma-mount-restrict');
        var opt = input.closest('.ma-mount__opt');
        var allowed = !restriction || restriction.split('|').indexOf(current) !== -1;
        if (opt) opt.hidden = !allowed;
        input.disabled = !allowed;
        if (!allowed && input.checked) { input.checked = false; needsResync = true; }
      }
      if (needsResync) {
        for (var j = 0; j < inputs.length; j++) {
          if (!inputs[j].disabled) { inputs[j].checked = true; break; }
        }
      }
      sync();
    }

    root.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute('data-ma-mount-input')) sync();
    });

    if (form) {
      form.addEventListener('change', function (e) {
        var input = e.target;
        if (!input || !input.classList || !input.classList.contains('ma-option-value__input')) return;
        if (decodeName(input.name).toLowerCase() === filterBy) applyRestrictFilter();
      });
    }

    applyRestrictFilter();

    if (!form || form.dataset.maAtcBound) return;
    form.dataset.maAtcBound = '1';
    /* 6 Oct 2026: the quote drawer (snippets/ma-dq-script.liquid) reads the chosen base from here. */
    window.maPickerUpgrade = function () { return selectedUpgrade(); };
    window.maPickerPropName = propName;

    function selectedUpgrade() {
      var sel = root.querySelector('[data-ma-mount-input]:checked');
      if (!sel || sel.getAttribute('data-upgrade') !== '1') return null;
      var id = resolvedVariantId(sel);
      if (!id) return null;
      return {
        variantId: Number(id),
        name: sel.value,
        img: sel.getAttribute('data-img') || '',
        qty: Math.max(1, parseInt(sel.getAttribute('data-qty'), 10) || 1),
        price: Number(String(resolvedPrice(sel)).split('$').join('')) || 0
      };
    }

    /* Everything the shopper typed or ticked that Shopify should record on the line. */
    function collectProperties() {
      var props = {};
      var els = form.querySelectorAll('[name^="properties["]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.disabled) continue;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
        var name = el.getAttribute('name') || '';
        var open = name.indexOf('[');
        var close = name.lastIndexOf(']');
        if (open < 0 || close <= open) continue;
        var key = name.slice(open + 1, close);
        if (!key || el.value === '' || el.value == null) continue;
        props[key] = el.value;
      }
      return props;
    }

    function unitPrice() {
      var select = form.querySelector('select[data-variants]');
      if (select && select.options[select.selectedIndex]) {
        var p = parseMoney(select.options[select.selectedIndex].textContent);
        if (p) return p;
      }
      var el = document.querySelector('[data-product-price-text]');
      return el ? parseMoney(el.textContent) : 0;
    }

    function setBusy(btn, on) {
      if (!btn) return;
      btn.disabled = !!on;
      if (on) btn.classList.add('processing'); else btn.classList.remove('processing');
    }

    document.addEventListener('submit', function (e) {
      if (e.target !== form) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var btn = form.querySelector('[data-product-atc]');
      var qtyField = form.querySelector('[name="quantity"]');
      var qty = qtyField ? parseInt(qtyField.value, 10) : 1;
      if (!qty || qty < 1) qty = 1;

      var idField = form.querySelector('[name="id"]');
      var variantId = idField ? Number(idField.value) : 0;
      if (!variantId) return;

      var upgrade = selectedUpgrade();
      var props = collectProperties();
      var recap = document.querySelector('[data-ma-recap]');
      var config = recap ? recap.textContent.trim() : '';

      var items = [{ id: variantId, quantity: qty, properties: props }];
      if (upgrade) {
        var mountProps = { For: productTitle() };
        mountProps[propName] = upgrade.name;
        if (upgrade.img) mountProps._mount_img = upgrade.img;
        items.push({ id: upgrade.variantId, quantity: qty * upgrade.qty, properties: mountProps });
      }

      /* Open before the network call, not after: this is what makes it feel instant. */
      var base = unitPrice();
      var rows = [{
        name: productTitle(),
        config: config,
        amount: qty + ' x ' + (base ? money(base * 100) : ''),
        img: productImage()
      }];
      if (upgrade) {
        rows.push({
          name: propName + ': ' + upgrade.name,
          config: (upgrade.qty > 1 ? upgrade.qty + ' pieces for your ' : 'Fitted to your ') + productTitle(),
          amount: qty + ' x ' + money(upgrade.price * 100),
          img: upgrade.img
        });
      }
      openPanel(rows);

      /* Exact, locally known, needs no server: this is the number the shopper cares
         about and it is correct from the first frame. */
      var addedCents = base * 100 * qty + (upgrade ? upgrade.price * 100 * qty : 0);
      var addedEl = document.querySelector('[data-ma-atc-added]');
      if (addedEl) addedEl.textContent = money(addedCents);

      setBusy(btn, true);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (body) {
            throw new Error(body.description || body.message || 'That could not be added to your cart.');
          });
        }
        return r.json();
      }).then(function () {
        return getCart();
      }).then(function (cart) {
        setBusy(btn, false);
        if (!cart) return;
        var sub = document.querySelector('[data-ma-atc-sub]');
        var subLine = document.querySelector('[data-ma-atc-cartsub]');
        if (sub && subLine) {
          sub.textContent = money(cart.items_subtotal_price);
          subLine.removeAttribute('hidden');
        }
        syncCartChrome(cart);
      }).catch(function (err) {
        setBusy(btn, false);
        panelError((err && err.message) || 'That could not be added to your cart. Please try again, or call us on 1300 515 620.');
      });
    }, true);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ma-mount]');
    if (!roots.length) return;
    roots.forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();